import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import { ArrowRight, Newspaper, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS_DE = ["","Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"];
const MONTHS_EN = ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const COLORS = ["#2C5E4E","#8B5E3C","#3B6B8C","#6B4E71","#4E7B5E","#7B5E4E"];

export default function LandingPage() {
  const { t, language } = useLanguage();
  const [newsletters, setNewsletters] = useState([]);
  const months = language === "de" ? MONTHS_DE : MONTHS_EN;

  useEffect(() => {
    axios.get(`${API}/public/newsletters`).then(r => setNewsletters(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-sand-50">
      <Header />

      {/* Hero Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-forest-900 mb-6">
              {t("landing.heroTitle")}
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-stone-600 mb-4">
              {t("landing.heroSubtitle")}
            </p>
            <p className="text-lg md:text-xl font-medium text-forest-800 mb-8">
              {language === "de" 
                ? "Möchten Sie an unserer Spark-Studie teilnehmen?" 
                : "Would you like to participate in our Spark study?"}
            </p>
            <Link to="/buchen">
              <Button 
                className="bg-forest-800 hover:bg-forest-900 text-white rounded-full px-8 py-6 text-lg transition-transform hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                data-testid="book-btn-hero"
              >
                {language === "de" ? "Spark Studie Termin Buchen" : "Book Spark Study Appointment"}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
          <div className="relative flex justify-center">
            <img
              src="/spark-logo.png"
              alt="Spark Studie"
              className="w-64 h-64 object-contain"
            />
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      {newsletters.length > 0 && (
        <section className="py-16 px-6 md:px-12 max-w-7xl mx-auto" data-testid="newsletter-section">
          <div className="flex items-center justify-between mb-8">
            <div>
              <span className="text-sm font-medium tracking-wide text-stone-500 uppercase mb-2 block">
                <Newspaper className="h-4 w-4 inline mr-1" />
                Newsletters
              </span>
              <h2 className="font-outfit text-2xl sm:text-3xl tracking-tight font-medium text-forest-800">
                {language === "de" ? "Unsere Newsletters" : "Our Newsletters"}
              </h2>
            </div>
            {newsletters.length > 6 && (
              <Link to="/newsletters" className="text-sm text-forest-700 hover:text-forest-900 font-medium flex items-center gap-1">
                {language === "de" ? "Alle anzeigen" : "View all"} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {newsletters.slice(0, 6).map((nl, idx) => (
              <Link key={nl.id} to={`/newsletter/${nl.id}`} className="group" data-testid={`newsletter-card-${nl.id}`}>
                <div className="rounded-2xl overflow-hidden shadow-sm border border-stone-200 transition-all group-hover:-translate-y-1 group-hover:shadow-lg">
                  {nl.image_url ? (
                    <div className="h-40 overflow-hidden">
                      <img src={nl.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                  ) : (
                    <div className="h-40 flex items-center justify-center" style={{ backgroundColor: nl.cover_color || COLORS[idx % COLORS.length] }}>
                      <div className="text-center text-white">
                        <p className="text-4xl font-outfit font-bold">{months[nl.month]}</p>
                        <p className="text-base opacity-80">{nl.year}</p>
                      </div>
                    </div>
                  )}
                  <div className="bg-white p-5">
                    <p className="text-xs text-stone-400 mb-1">{months[nl.month]} {nl.year}</p>
                    <h3 className="font-outfit font-medium text-forest-900 leading-snug line-clamp-2 group-hover:text-forest-700 transition-colors">
                      {language === "de" ? nl.title_de : nl.title_en}
                    </h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {newsletters.length > 6 && (
            <div className="text-center mt-8">
              <Link to="/newsletters">
                <Button variant="outline" className="rounded-full border-forest-300 text-forest-800 hover:bg-forest-50 px-6">
                  {language === "de" ? "Alle Newsletters anzeigen" : "View All Newsletters"} ({newsletters.length})
                </Button>
              </Link>
            </div>
          )}
        </section>
      )}

      {/* Contact Section */}
      <section className="py-16 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="bg-forest-800 rounded-2xl p-8 md:p-10 text-center">
          <Mail className="h-10 w-10 text-terracotta-500 mx-auto mb-4" />
          <h2 className="font-outfit text-2xl sm:text-3xl font-medium text-white mb-3">
            {t("landing.contactUs")}
          </h2>
          <p className="text-forest-100 mb-4 max-w-xl mx-auto">
            {t("landing.contactDesc")}
          </p>
          <p className="text-white font-medium">{t("landing.email")}: knpe@uni-trier.de</p>
          <p className="text-forest-100 text-sm mt-1">{t("landing.address")}</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-6 md:px-12 border-t border-stone-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <img 
              src="/lab-logo.png"
              alt="Logo"
              className="h-5 w-5 object-contain"
            />
            <span className="font-outfit font-medium text-forest-900 text-sm">Learning Brain Lab</span>
          </div>
          <p className="text-stone-500 text-xs">© 2026 Universität Trier. {t("landing.allRightsReserved")}</p>
        </div>
      </footer>
    </div>
  );
}
